import React, { useEffect } from "react";
// Static import — dynamic import() chunks never load in the Capacitor
// WKWebView (see src/lib/nativePush.ts). Guarded by isNative, which is
// iOS-only — @capacitor-community/apple-sign-in has no Android implementation.
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import { appleSignInAvailable, isIosNative } from "../lib/appleSignIn";
import { t } from "../i18n";

interface AppleButtonProps {
  onSuccess: (idToken: string, user?: { name?: string; email?: string }) => void;
  text?: "sign-in" | "sign-up";
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}

const AppleButton: React.FC<AppleButtonProps> = ({ onSuccess, text = "sign-in" }) => {
  const isNative = isIosNative();
  // None inside the Android app: it could never finish there (lib/appleSignIn).
  const available = appleSignInAvailable();

  useEffect(() => {
    if (isNative || !available) return;
    // Load Apple JS SDK for web
    if (!document.getElementById("apple-signin-sdk")) {
      const script = document.createElement("script");
      script.id = "apple-signin-sdk";
      script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, [isNative, available]);

  const handleClick = async () => {
    if (isNative) {
      // Use Capacitor Sign in with Apple plugin
      try {
        const result = await SignInWithApple.authorize({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID || "com.requesta.signin",
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || "https://www.requesta.app/auth/apple/callback",
          scopes: "email name",
        });

        if (result?.response?.identityToken) {
          onSuccess(result.response.identityToken, {
            name: result.response.givenName
              ? `${result.response.givenName} ${result.response.familyName || ""}`.trim()
              : undefined,
            email: result.response.email || undefined,
          });
        }
      } catch (err: any) {
        if (err?.message !== "The user canceled the sign-in flow.") {
          console.error("Apple Sign In error:", err);
        }
      }
    } else {
      // Web fallback
      try {
        if (!window.AppleID) {
          console.error("Apple JS SDK not loaded");
          return;
        }
        window.AppleID.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID || "com.requesta.signin",
          scope: "email name",
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || "https://www.requesta.app/auth/apple/callback",
          usePopup: true,
        });
        const res = await window.AppleID.auth.signIn();
        if (res?.authorization?.id_token) {
          onSuccess(res.authorization.id_token, {
            name: res.user?.name
              ? `${res.user.name.firstName || ""} ${res.user.name.lastName || ""}`.trim()
              : undefined,
            email: res.user?.email || undefined,
          });
        }
      } catch (err: any) {
        if (err?.error !== "popup_closed_by_user") {
          console.error("Apple Sign In web error:", err);
        }
      }
    }
  };

  if (!available) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      className="apple-signin-btn"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        width: "100%",
        height: "44px",
        padding: "0 20px",
        boxSizing: "border-box",
        background: "#fff",
        color: "#000",
        border: "none",
        borderRadius: "8px",
        fontSize: "15px",
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.51-3.23 0-1.44.64-2.2.45-3.06-.4C3.79 16.17 4.36 9.02 8.82 8.78c1.15.06 1.95.65 2.63.68.99-.2 1.95-.78 3.01-.7 1.28.1 2.24.61 2.87 1.56-2.64 1.57-2.01 5.02.36 5.98-.49 1.3-1.13 2.58-2.64 3.98zM12.03 8.7c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
      {text === "sign-up" ? t("appleButton.signUpWithApple") : t("appleButton.signInWithApple")}
    </button>
  );
};

export default AppleButton;
